import {
    EmailMessage,
    EmailMessageConversation,
    EmailMessageQuery,
    EmailMessageRecipient,
} from "../../../services/email.message.service";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Item } from "../../../pojo/Item";
import { S25Util } from "../../../util/s25-util";

/** Outlook/Exchange HTML class marking start of quoted reply/forward content in inbound messages. */
const OUTLOOK_QUOTED_REPLY_MARKER = "divRplyFwdMsg";

/** HR element Outlook inserts before quoted message history. */
const OUTLOOK_QUOTED_REPLY_HR_PATTERN = /<hr[\s/>]/i;

/** UI filter state for the email sent view. */
export interface EmailSentFilter {
    searchTerm: string;
    datePreset: "last7" | "last30" | "all" | "custom";
    customStartDate?: string | number | Date | null;
    customEndDate?: string | number | Date | null;
    organizations?: DropDownItem[];
    events?: DropDownItem[];
}

export const DEFAULT_EMAIL_SENT_FILTER: EmailSentFilter = {
    searchTerm: "",
    datePreset: "last30",
};

/** Filter preset scoped to a single event (e.g. Event Email History tab). */
export function buildFixedEventFilter(eventId: number): EmailSentFilter {
    return {
        ...DEFAULT_EMAIL_SENT_FILTER,
        events: [{ itemId: eventId, itemName: "" }],
    };
}

export interface RecipientListSummary {
    visible: string[];
    moreCount: number;
    all: string[];
}

export interface EmailSentListRow {
    conversation: EmailMessageConversation;
    displayMessage: EmailMessage | undefined;
    /** Plain-text list preview; computed once when the row is built. */
    preview: string;
}

function getRecipients(message: EmailMessage): EmailMessageRecipient[] {
    return S25Util.array.forceArray(message.email_message_recipient);
}

/** Strip HTML, decode entities, and collapse whitespace for plain-text previews. */
export function stripHtmlToPlainText(htmlBody: string | undefined): string {
    if (!htmlBody) {
        return "";
    }

    const normalizedHtml = htmlBody.replace(/<br\s*\/?>/gi, " ");
    const parsedDocument = new DOMParser().parseFromString(normalizedHtml, "text/html");
    const decodedText = parsedDocument.body.textContent ?? "";

    return decodedText
        .replace(/\u00a0/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function truncateWithEllipsis(text: string, maxLength: number): string {
    if (text.length <= maxLength) {
        return text;
    }

    return `${text.slice(0, maxLength)}…`;
}

/** Date/time format for email sent detail (no seconds or milliseconds). */
export const EMAIL_SENT_DATE_FORMAT = "MMM d, y, h:mm a";

/** Angular date pipe formats; not yet aligned with UserprefService date/time prefs. */
export const EMAIL_SENT_DATE_PART_FORMAT = "MMM d, y";
export const EMAIL_SENT_TIME_PART_FORMAT = "h:mm a";

export const THREAD_MESSAGE_PREVIEW_MAX_LENGTH = 120;

export interface ThreadMessagePartition {
    original: EmailMessage | undefined;
    replies: EmailMessage[];
}

export function getLatestMessage(messages: EmailMessage[]): EmailMessage | undefined {
    if (!messages.length) {
        return undefined;
    }

    return [...messages].sort((left, right) => -compareMessagesByReceivedAt(left, right))[0];
}

export function getConversationDisplayMessage(conversation: EmailMessageConversation): EmailMessage | undefined {
    return getLatestMessage(conversation.email_message ?? []);
}

export function getMessageSubject(message: EmailMessage | undefined): string {
    return message?.subject?.trim() ?? "";
}

export interface ReplyContextIds {
    itemId?: number;
    itemTypeId?: number;
}

export function buildReplySubject(headerMessage: EmailMessage | undefined): string {
    const subject = getMessageSubject(headerMessage);
    if (!subject) {
        return "Re:";
    }
    if (/^re:\s/i.test(subject)) {
        return subject;
    }
    return `Re: ${subject}`;
}

/** Shared conversation mailbox; must match inst_meta.email_conversation_mailboxes.mailbox_id for relay. */
export const CONVERSATION_RELAY_MAILBOX_ADDRESS = "series_conversation@collegenet.com";

export function appendConvoFooter(bodyHtml: string, convoUuid: string): string {
    const convoTag = `[convo:${convoUuid}]`;
    if (bodyHtml.includes(convoTag)) {
        return bodyHtml;
    }

    return `${bodyHtml}<p>${convoTag}</p>`;
}

export function getRelayMailboxRecipient(): string {
    const mailboxAddress = CONVERSATION_RELAY_MAILBOX_ADDRESS.trim();
    if (!mailboxAddress) {
        throw new Error("Conversation relay mailbox address is not configured");
    }

    return mailboxAddress;
}

/** Actual SMTP To address for relay-via-mailbox reply (conversation shared inbox). */
export function getReplyMailboxPreview(): string[] {
    return [getRelayMailboxRecipient()];
}

export function getReplyContextIds(messages: EmailMessage[], fixedEventId?: number): ReplyContextIds {
    for (const message of messages) {
        const eventId = message.event_id;
        if (eventId != null && Number.isFinite(eventId)) {
            return { itemId: eventId, itemTypeId: Item.Ids.Event };
        }
    }

    if (fixedEventId != null && Number.isFinite(fixedEventId)) {
        return { itemId: fixedEventId, itemTypeId: Item.Ids.Event };
    }

    return {};
}

export function buildSentListRow(conversation: EmailMessageConversation): EmailSentListRow {
    const displayMessage = getConversationDisplayMessage(conversation);
    return {
        conversation,
        displayMessage,
        preview: displayMessage ? getThreadMessagePreview(displayMessage) : "",
    };
}

export function compareMessagesByReceivedAt(left: EmailMessage, right: EmailMessage): number {
    const leftTime = new Date(left.received_at ?? 0).getTime();
    const rightTime = new Date(right.received_at ?? 0).getTime();
    return leftTime - rightTime;
}

export function getThreadOriginalMessage(messages: EmailMessage[]): EmailMessage | undefined {
    if (!messages.length) {
        return undefined;
    }

    const outboundMessages = messages.filter((message) => message.direction === "OUTBOUND");
    if (outboundMessages.length) {
        return [...outboundMessages].sort(compareMessagesByReceivedAt)[0];
    }

    return [...messages].sort(compareMessagesByReceivedAt)[0];
}

export function getThreadReplyMessages(messages: EmailMessage[]): EmailMessage[] {
    const originalMessage = getThreadOriginalMessage(messages);
    if (!originalMessage) {
        return [];
    }

    const originalMessageId = originalMessage.id;
    const replyMessages =
        originalMessageId != null
            ? messages.filter((message) => message.id !== originalMessageId)
            : messages.filter((message) => message !== originalMessage);

    return replyMessages.slice().sort((left, right) => -compareMessagesByReceivedAt(left, right));
}

export function partitionThreadMessages(messages: EmailMessage[]): ThreadMessagePartition {
    return {
        original: getThreadOriginalMessage(messages),
        replies: getThreadReplyMessages(messages),
    };
}

export function getThreadActionLabel(message: EmailMessage): string {
    return message.direction === "INBOUND" ? "replied" : "sent";
}

export function formatThreadCountLabel(messageCount: number): string {
    if (messageCount === 1) {
        return "1 message in thread";
    }
    return `${messageCount} messages in thread`;
}

export function getThreadMessageRoleLabel(message: EmailMessage): string {
    return message.direction === "OUTBOUND" ? "Sent" : "Reply";
}

export function getThreadMessageFromLabel(message: EmailMessage): string {
    return message.contact_name_from ?? message.from_address ?? "Unknown";
}

export function getThreadMessageToLabel(message: EmailMessage): string {
    return collectRecipientLabels(message).join(", ");
}

/** Plain-text snippet for list/detail previews; strips HTML and truncates. */
export function getThreadMessagePreview(
    message: EmailMessage,
    maxLength: number = THREAD_MESSAGE_PREVIEW_MAX_LENGTH,
): string {
    const plainText = stripHtmlToPlainText(message.body);
    if (!plainText) {
        return "";
    }
    return truncateWithEllipsis(plainText, maxLength);
}

/** Assumes {@code replies} is newest-first. Expands the latest reply when multiple exist. */
export function getDefaultExpandedMessageIds(replies: EmailMessage[]): Set<number> {
    const messageIds = replies
        .map((message) => message.id)
        .filter((messageId): messageId is number => messageId != null);

    if (!messageIds.length) {
        return new Set();
    }

    if (messageIds.length === 1) {
        return new Set(messageIds);
    }

    return new Set([messageIds[0]]);
}

export function getFallbackExpandedMessageIds(messages: EmailMessage[]): Set<number> {
    const latestMessage = getLatestMessage(messages);
    if (latestMessage?.id != null) {
        return new Set([latestMessage.id]);
    }

    return new Set();
}

/** True for inbound messages whose HTML body contains Outlook quoted-reply markers. */
export function messageHasQuotedReplyHtml(message: EmailMessage): boolean {
    if (message.direction !== "INBOUND") {
        return false;
    }

    const body = message.body ?? "";
    return body.includes(OUTLOOK_QUOTED_REPLY_MARKER) || OUTLOOK_QUOTED_REPLY_HR_PATTERN.test(body);
}

export function getRecipientsForContact(message: EmailMessage, contactId: number): EmailMessageRecipient[] {
    return getRecipients(message).filter((recipient) => recipient.cont_id_to === contactId);
}

function isRecipientUnread(recipient: EmailMessageRecipient, locallyReadRecipientIds?: ReadonlySet<number>): boolean {
    if (recipient.id != null && locallyReadRecipientIds?.has(recipient.id)) {
        return false;
    }

    return !S25Util.bool.isTrue(recipient.is_read);
}

export function isUnreadForContact(
    message: EmailMessage,
    contactId: number,
    locallyReadRecipientIds?: ReadonlySet<number>,
): boolean {
    return getRecipientsForContact(message, contactId).some((recipient) =>
        isRecipientUnread(recipient, locallyReadRecipientIds),
    );
}

export function getUnreadRecipientIdsForContact(
    message: EmailMessage,
    contactId: number,
    locallyReadRecipientIds?: ReadonlySet<number>,
): number[] {
    return getRecipientsForContact(message, contactId)
        .filter((recipient) => isRecipientUnread(recipient, locallyReadRecipientIds))
        .map((recipient) => recipient.id)
        .filter((recipientId): recipientId is number => recipientId != null);
}

export function countUnreadForContact(
    messages: EmailMessage[],
    contactId: number,
    locallyReadRecipientIds?: ReadonlySet<number>,
): number {
    return messages.filter((message) => isUnreadForContact(message, contactId, locallyReadRecipientIds)).length;
}

/** Expands every server-unread message for {@code contactId}; otherwise expands the latest message. */
export function getUnreadExpandedMessageIds(messages: EmailMessage[], contactId: number): Set<number> {
    const unreadMessageIds = messages
        .filter((message) => isUnreadForContact(message, contactId))
        .map((message) => message.id)
        .filter((messageId): messageId is number => messageId != null);

    if (unreadMessageIds.length) {
        return new Set(unreadMessageIds);
    }

    return getFallbackExpandedMessageIds(messages);
}

export function formatUnreadCountLabel(unreadCount: number): string {
    if (unreadCount <= 0) {
        return "";
    }

    if (unreadCount === 1) {
        return "1 unread";
    }

    return `${unreadCount} unread`;
}

export function formatTotalCountLabel(totalCount: number): string {
    if (totalCount <= 0) {
        return "";
    }

    if (totalCount === 1) {
        return "1 Message";
    }

    return `${totalCount} Messages`;
}

/** Messages visible in this conversation payload (list row or loaded thread). */
export function getConversationVisibleMessageCount(conversation: EmailMessageConversation): number {
    const visibleCount = conversation.email_message?.length ?? 0;
    if (visibleCount > 0) {
        return visibleCount;
    }

    return conversation.message_count ?? 0;
}

function recipientDisplayLabel(recipient: EmailMessageRecipient): string | undefined {
    return recipient.contact_name_to ?? recipient.recipient_address;
}

function collectRecipientLabels(message: EmailMessage): string[] {
    const labels: string[] = [];
    for (const recipient of getRecipients(message)) {
        const label = recipientDisplayLabel(recipient);
        if (label) {
            labels.push(label);
        }
    }

    if (labels.length) {
        return labels;
    }

    return [message.to_address ?? "Unknown"];
}

/** Up to {@code maxVisible} recipient labels at equal weight, with overflow count. */
export function getRecipientListSummary(message: EmailMessage, maxVisible: number = 2): RecipientListSummary {
    const all = collectRecipientLabels(message);
    const visible = all.slice(0, maxVisible);
    const moreCount = Math.max(0, all.length - maxVisible);

    return { visible, moreCount, all };
}

export function buildPaginationRangeLabel(
    pageNum: number,
    pageCount: number,
    totalResults: number,
    itemsOnPage: number,
): string {
    if (totalResults <= 0 || itemsOnPage <= 0) {
        return "Showing 0 conversations";
    }

    if (pageNum === pageCount) {
        const rangeEnd = totalResults;
        const rangeStart = Math.min(Math.max(1, totalResults - itemsOnPage + 1), rangeEnd);
        return `Showing ${rangeStart}–${rangeEnd} of ${totalResults} conversations`;
    }

    const rangeStart = (pageNum - 1) * itemsOnPage + 1;
    const rangeEnd = pageNum * itemsOnPage;

    return `Showing ${rangeStart}–${rangeEnd} of ${totalResults} conversations`;
}

export function buildEmailQuery(filter: EmailSentFilter, contactId: number): EmailMessageQuery {
    const query: EmailMessageQuery = {};

    query.cont_id = [String(contactId)];

    const trimmedSearch = filter.searchTerm.trim();
    if (trimmedSearch) {
        query.search_term = [trimmedSearch];
    }

    if (filter.datePreset === "last7") {
        query.created_dt_start = daysAgoIsoDate(7);
        query.created_dt_end = tomorrowIsoDate();
    } else if (filter.datePreset === "last30") {
        query.created_dt_start = daysAgoIsoDate(30);
        query.created_dt_end = tomorrowIsoDate();
    } else if (filter.datePreset === "custom") {
        const customStart = pickerValueToIsoDate(filter.customStartDate);
        const customEnd = pickerValueToIsoDate(filter.customEndDate);
        if (customStart) {
            query.created_dt_start = customStart;
        }
        if (customEnd) {
            query.created_dt_end = customEnd;
        }
    }

    const organizationIds = mapDropdownIds(filter.organizations);
    if (organizationIds.length) {
        query.organization_id = organizationIds;
    }

    const eventIds = mapDropdownIds(filter.events);
    if (eventIds.length) {
        query.event_id = eventIds;
    }

    return query;
}

function mapDropdownIds(items: DropDownItem[] | undefined): string[] {
    if (!items?.length) {
        return [];
    }

    const ids: string[] = [];
    for (const item of items) {
        const itemId = item.itemId;
        if (itemId != null && itemId !== "") {
            ids.push(String(itemId));
        }
    }
    return ids;
}

/** Normalizes date-picker values to yyyy-MM-dd for the email query API. */
function pickerValueToIsoDate(value: string | number | Date | null | undefined): string | undefined {
    if (value == null || value === "") {
        return undefined;
    }

    const parsed = S25Util.date.parse(value);
    if (!parsed) {
        return undefined;
    }

    return S25Util.date.toS25ISODateStr(parsed)!;
}

function tomorrowIsoDate(): string {
    return S25Util.date.toS25ISODateStr(S25Util.date.addDays(new Date(), 1))!;
}

function daysAgoIsoDate(daysAgo: number): string {
    return S25Util.date.toS25ISODateStr(S25Util.date.addDays(new Date(), -daysAgo))!;
}
